<template>
  <page-block v-if="activeAccount">
    <property-block content-max-width="100%">
      <template #text>
        <div class="h4 text-secondary mb-1">
          Аккаунт
          <skeleton-loader v-if="!activeAccount" inline width="100px" height="24px" />
          <template v-else>
            {{ activeAccount.contractNumber }}
          </template>
        </div>
        <skeleton-loader v-if="!activeAccount" inline width="100px" height="21px" />
        <account-status v-else :status="activeAccount.status" />
      </template>
      <template #content>
        <div class="object__row">
          <property-item class="object__coll-min" title="Юридическое лицо">
            <skeleton-loader v-if="!activeAccount" width="220px" type="paragraph-m_h" />
            <template v-else>
              {{ activeAccount.legal.name[0].value }}
            </template>
          </property-item>
          <property-item class="object__coll-min" title="Тип договора">
            {{ 
              activeAccount.contractType === ContractType.Agency ? 'Агентский' 
              : activeAccount.contractType === ContractType.Sale ? 'Купля-продажа «Нетто тариф»' 
              : activeAccount.contractType === ContractType.SaleGross ? 'Купля-продажа «Брутто тариф»' 
              : activeAccount.contractType === ContractType.Booking ? 'Бронирования' 
              : '—'
            }}
            <help-tooltip class="ml-1" max-width="300">
              <template v-if="activeAccount.contractType === ContractType.Agency">
                <p class="paragraph-s_h font-600 mb-2">Договор агентский</p>
                <p class="paragraph-xs">
                  Тарифные планы заводятся как <b>«Брутто тариф»</b>. Применяется схема для взаиморасчётов — 80% от цены
                  переданной в EXTRANET плюс агентское вознаграждение 50 руб., предоставляемое за реализацию Агентом
                  услуг. Цена продажи равна <b>«Брутто тарифу»</b>
                </p>
              </template>
              <template v-else-if="activeAccount.contractType === ContractType.Sale">
                <p class="paragraph-s_h font-600 mb-2">Договор купля-продажа</p>
                <p class="paragraph-xs">
                  Тарифные планы заводятся как <b>«Нетто тариф»</b>. Применяется схема для взаиморасчётов по
                  номинальному тарифу. Цену продажи регулирует Алеан.
                </p>
              </template>
              <template v-else-if="activeAccount.contractType === ContractType.SaleGross">
                <p class="paragraph-s_h font-600 mb-2">Договор купля брутто</p>
                <p class="paragraph-xs">
                  Применяется схема для взаиморасчётов&nbsp;-&nbsp;<b>«Брутто тариф»</b> минус скидка 20 % от цены, переданной в EXTRANET. Цена на поиске равна <b>«Брутто тарифу»</b>
                </p>
              </template>
            </help-tooltip>
          </property-item>
        </div>

        <div class="object__row mt-4">
          <property-item class="object__coll-min" title="Способ расчёта"> Оплата туроператором </property-item>
          <property-item class="object__coll-min" title="Валюта расчёта">
            <skeleton-loader v-if="!activeAccount" width="100px" type="paragraph-m_h" />
            <template v-else>
              {{ getCurrencyText(activeAccount.currencyISOCode) }}
            </template>
          </property-item>
        </div>
        <e-btn
          v-if="activeAccount?.hasOffer"
          prepend-icon-src="download"
          variant="link"
          :loading="isGetOfferContractLoading"
          @click="openOfferContract"
        >
          Договор оферты
        </e-btn>
      </template>
    </property-block>
  </page-block>

  <div v-if="accommodationObject && isUnknownStatus" class="object__unknown">
    <div class="h3 mb-6 mt-3">
      Анкета
      <status :status="accommodationObject.commonInformation.status" />
    </div>
  </div>

  <page-block full-page>
    <div class="font-600">
      <property-block content-max-width="460px">
        <template #text>
          <div class="text-secondary h4">
            <b> Объект размещения </b>
          </div>
          <status
            v-if="accommodationObject && !isUnknownStatus"
            :status="accommodationObject.commonInformation.status"
          />
          <skeleton-loader v-if="!accommodationObject" width="100px" height="21px" />
        </template>
        <template #content>
          <property-item
            class="mb-4 mb-md-6"
            :title="!isUnknownStatus ? 'Полное наименование объекта' : 'Наименование'"
          >
            <skeleton-loader v-if="!accommodationObject" width="220px" type="paragraph-m_h" />
            <multilanguage-text v-else :text="accommodationObject.commonInformation.name">
              <template #default="{ value }">
                {{ value }}
              </template>
            </multilanguage-text>
          </property-item>

          <div class="object__type">
            <property-item :title="!isUnknownStatus ? 'Тип объекта размещения' : 'Тип'">
              <skeleton-loader v-if="!accommodationObject" width="220px" type="paragraph-m_h" />
              <template v-else>
                {{ accommodationObject.commonInformation.type }}
              </template>
            </property-item>
            <property-item v-if="!isUnknownStatus" title="Категория (звёздность)">
              <skeleton-loader v-if="!accommodationObject" width="220px" type="paragraph-m_h" />
              <template v-else>
                {{ accommodationObject.commonInformation.category }}
              </template>
            </property-item>
          </div>
          <property-item :title="!isUnknownStatus ? 'Населённый пункт' : 'Адрес'">
            <skeleton-loader v-if="!accommodationObject" width="220px" type="paragraph-m_h" />
            <template v-else>
              {{ accommodationObject.commonInformation.town }}
            </template>
          </property-item>
        </template>
      </property-block>

      <v-spacer />
      <v-img
        v-if="false"
        src="https://s.101hotelscdn.ru/uploads/image/hotel_10/308934/18826755.jpg"
        aspect-ratio="1"
        cover
        class="object__img"
      />
    </div>
  </page-block>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import PageBlock from '@/core/components/interface/PageBlock.vue';
import PropertyBlock from '@/core/components/interface/PropertyBlock.vue';
import PropertyItem from '@/core/components/interface/PropertyItem.vue';
import SkeletonLoader from '@/core/components/interface/SkeletonLoader.vue';
import { EBtn, HelpTooltip, MultilanguageText } from '@/core/components/ui';
import { getCurrencyText } from '@/core/utils/currency';
import { openDocument } from '@/core/utils/file';
import { ContractType } from '@/graphql/generated';
import AccountStatus from '@/parts/objects/components/AccountStatus.vue';
import Status from '@/parts/objects/components/Status.vue';
import { useAccommodationObjectsStore } from '@/parts/objects/store/accommodationObjects';
import { useOfferContractsStore } from '@/parts/objects/store/offerContracts';

const { accommodationObject, isUnknownStatus, activeAccount } = useAccommodationObjectsStore();

const { getAccountOfferContract } = useOfferContractsStore();

const isGetOfferContractLoading = ref(false);

const openOfferContract = async () => {
  if (!activeAccount.value) {
    return;
  }

  isGetOfferContractLoading.value = true;

  const result = await getAccountOfferContract(activeAccount.value.contractNumber);

  isGetOfferContractLoading.value = false;

  if (!result.errors && result.data?.getAccountOfferContract?.data) {
    openDocument(result.data.getAccountOfferContract.data, `Договор оферты ${activeAccount.value.contractNumber}`);
  }
};
</script>

<style scoped lang="scss">
.object {
  font-weight: 600;

  &__type {
    display: flex;
    justify-content: space-between;

    width: 100%;
    margin-bottom: 24px;

    @include sm-and-down {
      flex-direction: column;

      row-gap: 16px;

      margin-bottom: 16px;
    }
  }

  &__unknown {
    @include sm-and-down {
      padding: 0 24px;
    }

    @include xs {
      padding: 0 16px;
    }
  }

  &__row {
    display: flex;
    flex-wrap: wrap;
    gap: 16px 40px;
  }

  &__coll-min {
    width: 100%;
    max-width: 240px;

    font-weight: 600;

    @include sm-and-down {
      width: fit-content;
      max-width: initial;
    }
  }

  &__img {
    flex-shrink: 0;

    width: 336px;
    max-width: 50%;
    margin-left: $gap-3;

    border-radius: $border-radius-l;

    @include lg-and-down {
      width: 282px;
    }
    @include sm-and-down {
      width: 250px;
    }
    @include extra-xs {
      width: 100%;
      max-width: 100%;
      margin-top: $gap-8;
      margin-left: 0;
    }
  }
}
</style>
